import { useCallback, useEffect } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { useDispatch } from 'react-redux';

import { useAppSelector } from 'src/app/store/hooks';
import DateRangeBadge from 'src/common/date/DateRangeBadge';
import { FilterGroupRead } from 'src/common/external/bambi-api/bambiApi';
import { FilterGroup } from 'src/common/FilterGroup/FilterGroup';
import { useInitializeFilterGroup } from 'src/common/FilterGroup/useInitializeFilterGroup';
import { FilterPopoverTrigger } from 'src/common/primitives/FilterPopover/FilterPopoverTrigger';
import { RadioGroupFilterPopover } from 'src/common/primitives/FilterPopover/RadioGroupFilterPopover';
import { useUserRoles } from 'src/features/auth/useUserRoles';

import {
  createdAtDateRangeStorageKey,
  dateRangeStorageKey,
  driverStorageKey,
  InvoicedTripsValue,
  payersStorageKey,
  priceStorageKey,
  statusesStorageKey,
  tagsStorageKey,
} from '../constants';
import { useRestoreFiltersFromStorage } from '../finder/useRestoreFiltersFromStorage';
import {
  clearFilters,
  initializeFilters,
  setFilterInvoicedTrips,
  setFilterPrice,
  setSelectedCreatedAtDateRange,
  setSelectedDateRange,
  setSelectedDrivers,
  setSelectedFilterGroup,
  setSelectedPayers,
  setSelectedStatuses,
  setSelectedTags,
} from '../trip.slice';

export function TripManagementFilters() {
  const dispatch = useDispatch();
  const { isRideOrderingPortalUser } = useUserRoles();

  // TODO break out into individual selectors, or reselect
  const {
    selectedDateRange,
    selectedPayers,
    selectedStatuses,
    selectedTags,
    selectedDrivers,
    uploadInProgress,
    filterVehicles,
    filterPrice,
    filterIncludeNote,
    filterSpaceTypes,
    filterCanceledReasons,
  } = useAppSelector((state) => state.trip);

  const selectedCreatedAtDateRange = useAppSelector(
    (state) => state.trip.selectedCreatedAtDateRange
  );
  const filterInvoicedTrips = useAppSelector(
    (state) => state.trip.filterInvoicedTrips
  );

  // Initialize filters from URL
  useEffect(() => {
    dispatch(initializeFilters());
  }, [dispatch]);

  const handleInitializeFilterGroup = useCallback(
    (group: FilterGroupRead) => {
      dispatch(setSelectedFilterGroup(group));
    },
    [dispatch]
  );

  // Initialize filter groups from URL
  useInitializeFilterGroup(handleInitializeFilterGroup);

  // Transfer stored filters into state
  useRestoreFiltersFromStorage([
    [payersStorageKey, setSelectedPayers],
    [statusesStorageKey, setSelectedStatuses],
    [tagsStorageKey, setSelectedTags],
    [dateRangeStorageKey, setSelectedDateRange],
    [createdAtDateRangeStorageKey, setSelectedCreatedAtDateRange],
    [driverStorageKey, setSelectedDrivers],
    [priceStorageKey, setFilterPrice],
  ]);

  const handleClearClick = () => {
    dispatch(clearFilters());
  };

  const isFilteringInvoicedTrips =
    filterInvoicedTrips !== InvoicedTripsValue.ALL_TRIPS;

  const showClearFiltersButton = !!(
    selectedPayers.length > 0 ||
    selectedStatuses.length > 0 ||
    selectedTags.length > 0 ||
    selectedDrivers.length > 0 ||
    selectedDateRange?.from ||
    selectedCreatedAtDateRange?.from ||
    isFilteringInvoicedTrips ||
    filterVehicles.length > 0 ||
    filterPrice?.min ||
    filterPrice?.max ||
    filterIncludeNote ||
    filterSpaceTypes.length > 0 ||
    filterCanceledReasons.length > 0
  );

  return (
    <div className="flex items-center gap-2 pt-4">
      <FilterGroup
        filterset="TripFilter"
        filters={makeFilterGroupFilters({
          rangeStart: selectedDateRange?.from,
          rangeEnd: selectedDateRange?.to,
          payers: selectedPayers,
          statuses: selectedStatuses,
          tags: selectedTags,
          hasInvoice: filterInvoicedTrips,
        })}
        onSelectFilterGroup={(group) => {
          dispatch(clearFilters());
          dispatch(setSelectedFilterGroup(group));
        }}
      />
      {selectedDateRange?.from && selectedDateRange.to && (
        <div>
          <DateRangeBadge
            from={selectedDateRange.from}
            to={selectedDateRange.to}
            onClear={() => {
              dispatch(setSelectedDateRange(undefined));
            }}
          />
        </div>
      )}
      {!isRideOrderingPortalUser && (
        <RadioGroupFilterPopover
          triggerElement={
            <FilterPopoverTrigger
              label="Invoice"
              showCountBadge={false}
              count={isFilteringInvoicedTrips ? 1 : 0}
            />
          }
          value={filterInvoicedTrips ?? InvoicedTripsValue.ALL_TRIPS}
          onChange={(value) => {
            dispatch(setFilterInvoicedTrips(value));
          }}
          options={[
            {
              label: 'All Trips',
              value: InvoicedTripsValue.ALL_TRIPS,
            },
            {
              label: 'Trips with Invoice',
              value: InvoicedTripsValue.INVOICED_TRIPS,
            },
            {
              label: 'Trips without Invoice',
              value: InvoicedTripsValue.NOT_INVOICED_TRIPS,
            },
            {
              label: 'Trips with Paid Invoice',
              value: InvoicedTripsValue.PAID_INVOICE_TRIPS,
            },
          ]}
        />
      )}

      {showClearFiltersButton && (
        <Button onClick={handleClearClick} variant="anchor">
          clear filters
        </Button>
      )}
      {uploadInProgress && (
        <div className="ml-12 flex items-center justify-center text-center">
          <LoadingIndicator />
          <span className="ml-4">Trip Upload in Progress..</span>
        </div>
      )}
    </div>
  );
}

interface MakeFilterGroupFiltersProps {
  rangeStart?: string;
  rangeEnd?: string;
  payers?: string[];
  statuses?: string[];
  tags?: string[];
  hasInvoice?: InvoicedTripsValue;
}

/**
 * Creates a set of filters which match the API filters
 */
function makeFilterGroupFilters({
  rangeStart,
  rangeEnd,
  payers,
  statuses,
  tags,
  hasInvoice,
}: MakeFilterGroupFiltersProps) {
  return {
    range_start: rangeStart,
    range_end: rangeEnd,
    payer__in: payers,
    status__in: statuses,
    tag__in: tags,
    has_invoice: hasInvoice,
  };
}
